import React from 'react'
import styled from '@emotion/styled'
import { colors, font, radius, shadow, spacing } from '@mobi/component-library/Theme/Common'
import { SharedProps } from '../types'
import { Icon } from '../../Icon'

type Props = SharedProps & {
  value: string
  onChange: React.ChangeEventHandler<HTMLTextAreaElement>
  characterCount?: number
  /** The placeholder text for textarea (shown when empty) */
  placeholder?: string
  isInvalid?: boolean
}

const enum LocalConstants {
  DisabledClassName = 'input__textarea--disabled',
  HelpTextClassName = 'input__textarea__help',
  CharacterCountClassName = 'input__textarea__char-count',
  InputErrorClassName = 'input__error',
  ErrorTextClassName = 'input__error-text',
}

export const TextArea: React.FC<Props> = ({
  label,
  value,
  name,
  placeholder,
  isDisabled,
  helpText,
  onChange,
  characterCount,
  isInvalid,
}) => {
  const id = React.useId()
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)

  const handleRenderInputSize = React.useCallback(() => {
    if (!textareaRef.current || !textareaRef.current.parentNode) return
    // @ts-expect-error Type issue
    textareaRef.current.parentNode.dataset.replicatedValue = textareaRef.current.value
  }, [])

  React.useEffect(() => {
    handleRenderInputSize()
  }, [handleRenderInputSize])

  const classNames = []
  if (isDisabled) classNames.push(LocalConstants.DisabledClassName)
  if (isInvalid) classNames.push(LocalConstants.InputErrorClassName)

  return (
    <WrapperStyled className={classNames.join(' ')}>
      {label ? <label htmlFor={id}>{label}</label> : null}

      <div data-js-character-count={!!characterCount}>
        <textarea
          id={id}
          value={value}
          onChange={evt => {
            if (characterCount && evt.currentTarget.value.length > characterCount - 1) {
              evt.currentTarget.value = evt.currentTarget.value.substring(0, characterCount)
            }
            onChange(evt)
          }}
          ref={textareaRef}
          name={name}
          placeholder={placeholder}
          disabled={isDisabled}
          rows={1}
          onInput={handleRenderInputSize}
        />

        {characterCount ? (
          <span className={LocalConstants.CharacterCountClassName}>
            {value?.length || 0}/{characterCount}
          </span>
        ) : null}
      </div>

      <span
        className={isInvalid ? LocalConstants.ErrorTextClassName : LocalConstants.HelpTextClassName}
      >
        {isInvalid ? <Icon name='LineAlertCircle' size='1.6rem' /> : null}
        {helpText}
      </span>
    </WrapperStyled>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.smx1,
  color: colors.black,
  fontFamily: font.family.primary,
  fontSize: font.size.md.fontSize,
  letterSpacing: font.size.md.letterSpacing,
  lineHeight: font.size.md.lineHeight,

  '> label': {
    display: 'block',
    fontWeight: font.weight.medium,
    fontSize: font.size.lg.fontSize,
    letterSpacing: font.size.lg.letterSpacing,
    lineHeight: font.size.lg.lineHeight,
  },

  '> div': {
    position: 'relative',
    display: 'grid',

    '::after': {
      content: 'attr(data-replicated-value) " "',
      whiteSpace: 'pre-wrap',
      visibility: 'hidden',
    },

    '> textarea': {
      resize: 'none',
      overflow: 'hidden',
    },

    '> textarea, ::after': {
      gridArea: '1 / 1 / 2 / 2',
      display: 'flex',
      alignItems: 'center',
      minHeight: '12rem',
      backgroundColor: colors.white,
      border: `1px solid ${colors.neutral['300']}`,
      caretColor: colors.studio['500'],
      color: colors.black,
      borderRadius: radius.lgx1,
      boxShadow: shadow.xs,
      padding: `${spacing.sm} ${spacing.md}`,
      outlineColor: 'transparent',
      outlineStyle: 'solid',
      outlineWidth: '0.4rem',
      fontWeight: font.weight.regular,
      fontSize: font.size.lg.fontSize,
      letterSpacing: font.size.lg.letterSpacing,
      lineHeight: font.size.lg.lineHeight,
      wordBreak: 'break-all',

      '::placeholder': {
        color: colors.neutral['500'],
      },
    },

    '> textarea:focus': {
      borderColor: colors.lavender['300'],
      outlineColor: colors.lavender['50'],
    },

    '&[data-js-character-count="true"]': {
      '> textarea, ::after': {
        paddingBottom: spacing.bigx3,
      },
    },

    [`.${LocalConstants.CharacterCountClassName}`]: {
      position: 'absolute',
      right: spacing.md,
      bottom: spacing.sm,
      padding: '0.2rem 0.4rem',
      borderRadius: radius.pill,
      color: colors.neutral[800],
      font: 'inherit',
      fontSize: 'inherit',
      fontWeight: font.weight.medium,
      border: `1px solid ${colors.neutral[300]}`,
      backgroundColor: colors.neutral[200],
    },
  },

  [`.${LocalConstants.HelpTextClassName}`]: {
    color: colors.neutral['600'],
    fontSize: font.size.md.fontSize,
    letterSpacing: font.size.md.letterSpacing,
    lineHeight: font.size.md.lineHeight,

    '&:empty': { display: 'none' },
  },

  [`&.${LocalConstants.DisabledClassName}`]: {
    color: colors.neutral['400'],

    '> div > textarea': {
      color: 'inherit',
      backgroundColor: colors.surface['100'],

      '::placeholder': { color: 'inherit' },
    },

    [`.${LocalConstants.HelpTextClassName}`]: { color: 'inherit' },
  },

  [`&.${LocalConstants.InputErrorClassName}`]: {
    '& > div': {
      '> textarea, ::after': {
        background: colors.error[50],
        border: `1px solid ${colors.error[600]}`,
      },
    },

    [`.${LocalConstants.ErrorTextClassName}`]: {
      display: 'flex',
      justifyContent: 'flex-start',
      gap: spacing.smx2,
      fontFamily: font.family.primary,
      fontSize: font.size.md.fontSize,
      fontWeight: font.weight.regular,
      lineHeight: font.size.md.lineHeight,
      color: colors.error['600'],
    },
  },
})
